import { Popover } from './ui/Popover'

/**
 * The explanation behind a number, reachable on a touch screen.
 *
 * **What this replaces.** Twenty `title` attributes carried the second layer of this entire site:
 * the subtraction behind an edge, the fair odds behind a break-even price, the opening price behind
 * a line move, what the sharp book actually said, why a stake came back, what a tick meant, how many
 * rated games a player had. `title` renders nothing on a touch screen — no long-press, no tap, no
 * indication it exists. So the device this site was built for got the conclusions and none of the
 * workings, which inverts the one promise the product makes: *every suggestion shows the arithmetic
 * behind it precisely so you can disagree with it.*
 *
 * **Why a component and not a tooltip library.** The behaviour was already written and already
 * correct in `Term`; it needed a second caller, not a dependency. Both now sit on {@link Popover}.
 *
 * **The affordance is deliberately quieter than a glossary word.** `Term` underlines in the accent,
 * because a word you may not know is worth interrupting a sentence for. A number that carries its
 * own derivation is not — the reader already understands "1.53", and the dotted rule is an offer
 * rather than a flag. Two different marks so the reader can tell "define this word" from "show me
 * how this was calculated" before tapping.
 *
 * Prefer printing a short fact outright over hiding it in here. The edge column now shows
 * `61% vs 51%` in the row; only the sentence explaining what that means lives behind a tap.
 */
export function Detail({
  label,
  heading,
  children,
  className = '',
}: {
  /** Announced to a screen reader in place of the trigger's visual content. Say what opens. */
  label: string
  /** The trigger — the number, chip or glyph the reader sees. */
  heading: React.ReactNode
  /** The explanation. */
  children: React.ReactNode
  className?: string
}) {
  return (
    <Popover
      label={label}
      trigger={heading}
      triggerClassName={`border-b border-dotted border-line-strong hover:border-solid hover:border-ink-faint ${className}`}
    >
      <span className="block text-xs leading-relaxed text-ink-soft">{children}</span>
    </Popover>
  )
}
